/**
 * DEV-260 — regras de tela por etapa. Tudo sai da definição do Core; aqui só
 * se aplica a condicional de tela (espelho de `form-rules`) e se monta o que
 * vai para o servidor. Nenhuma lista de etapas, campos ou obrigatoriedade.
 */
import {
  CONDITIONAL_DEPENDENTS,
  CONDITIONALLY_REQUIRED,
  isQuestionShown,
  normalizeLabel,
  type Answers,
} from './form-rules';
import {
  isEmptyAnswer,
  requiredFor,
  validateSubmission,
  type AnswerIssue,
} from './answer-validation';
import type { FormDefinition, StandardFormPage } from './definition';
import { FIELD_MESSAGES } from './field-copy';
import { omitKeys } from './objects';

export function findPage(def: FormDefinition, pageName: string) {
  return def.pages.find((p) => p.name === pageName) ?? null;
}

function standardPage(def: FormDefinition, pageName: string): StandardFormPage | null {
  const page = findPage(def, pageName);
  return page && page.kind === 'standard' ? page : null;
}

/** Aplica uma resposta e remove os dependentes que deixaram de estar à mostra. */
export function applyAnswerChange(answers: Answers, question: string, value: unknown): Answers {
  let next: Answers = isEmptyAnswer(value)
    ? omitKeys(answers, [question])
    : { ...answers, [question]: value };

  const queue = [question];
  while (queue.length > 0) {
    const governor = queue.shift() as string;
    for (const dependent of CONDITIONAL_DEPENDENTS[governor] ?? []) {
      if (!isQuestionShown(dependent, next) && dependent in next) {
        next = omitKeys(next, [dependent]);
        queue.push(dependent);
      }
    }
  }
  return next;
}

/** Exigência do tipo escolhido (rótulo casado sem caixa e acento, como no Core). */
export function requiredSet(def: FormDefinition, answers: Answers): Set<string> {
  return requiredFor(def, answers);
}

/**
 * A pergunta recebe "(obrigatório)" agora? Exigida pela definição E à mostra.
 * `areas_curso`/`cursos` são regra de grupo (AC-46): nenhum dos dois é marcado.
 */
export function isRequiredNow(def: FormDefinition, answers: Answers, question: string): boolean {
  if (!isQuestionShown(question, answers)) return false;
  if (question === 'areas_curso' || question === 'cursos') return false;
  return requiredSet(def, answers).has(question);
}

/** Erros da etapa, na ordem da tela (mesma validação do envio, filtrada). */
export function pageIssues(def: FormDefinition, pageName: string, answers: Answers): AnswerIssue[] {
  const page = findPage(def, pageName);
  if (!page || page.kind !== 'standard') return [];
  const order = new Map(page.questions.map((q, i) => [q.name, i]));
  return validateSubmission(def, answers)
    .filter((i) => i.page === pageName)
    .sort((a, b) => (order.get(a.question) ?? 0) - (order.get(b.question) ?? 0));
}

/** Tipos em que filtrar por idade tem exigência legal (AC-51). */
const AGE_FILTER_CONTRACT_TYPES = new Set(['jovem aprendiz', 'estagio']);

/**
 * Avisos que não bloqueiam: meta menor que posições (23074) e idade (23090,
 * AC-50/51). O aviso de filtro fica sob a idade preenchida (a primeira, quando
 * são duas) e o de limite em aberto sob a idade vazia — assim os dois aparecem
 * juntos quando valem juntos (research.md D22; a tela do Figma não fixa o lugar).
 */
export function pageWarnings(answers: Answers): Record<string, string> {
  const out: Record<string, string> = {};
  const goal = answers.meta_inscritos;
  const positions = answers.numero_posicoes;
  if (typeof goal === 'number' && typeof positions === 'number' && goal >= 1 && goal < positions) {
    out.meta_inscritos = FIELD_MESSAGES.metaMenorQuePosicoes;
  }

  const hasMin = !isEmptyAnswer(answers.idade_minima);
  const hasMax = !isEmptyAnswer(answers.idade_maxima);
  if (hasMin || hasMax) {
    const type = typeof answers.tipo_contratacao === 'string' ? answers.tipo_contratacao : '';
    if (!AGE_FILTER_CONTRACT_TYPES.has(normalizeLabel(type))) {
      out[hasMin ? 'idade_minima' : 'idade_maxima'] = FIELD_MESSAGES.idadeSemExigencia;
    }
    if (hasMin !== hasMax) {
      out[hasMin ? 'idade_maxima' : 'idade_minima'] = FIELD_MESSAGES.idadeEmAberto;
    }
  }
  return out;
}

/** Corpo do PATCH da etapa: só as perguntas dela; escondida ou vazia vai como `null`. */
export function pageAnswersForSave(
  def: FormDefinition,
  pageName: string,
  answers: Answers
): Answers {
  const page = standardPage(def, pageName);
  const out: Answers = {};
  for (const q of page?.questions ?? []) {
    const value = answers[q.name];
    out[q.name] = isQuestionShown(q.name, answers) && !isEmptyAnswer(value) ? value : null;
  }
  return out;
}

export function nextPageName(def: FormDefinition, pageName: string): string | null {
  const index = def.pages.findIndex((p) => p.name === pageName);
  return def.pages[index + 1]?.name ?? null;
}

export function previousPageName(def: FormDefinition, pageName: string): string | null {
  const index = def.pages.findIndex((p) => p.name === pageName);
  return index > 0 ? (def.pages[index - 1]?.name ?? null) : null;
}

/** Posição da etapa sobre o total (etapas visíveis + resumo). O Figma não fixa regra (research.md D18). */
export function progressPercent(def: FormDefinition, pageName: string | 'resumo'): number {
  const total = def.pages.length + 1;
  const index =
    pageName === 'resumo' ? def.pages.length : def.pages.findIndex((p) => p.name === pageName);
  return Math.round(((index + 1) / total) * 100);
}

export { CONDITIONALLY_REQUIRED };
